import React, { useState, useEffect } from 'react'
import { Box, LinearProgress, Typography } from "@mui/material"
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Mention from '@tiptap/extension-mention'
import { EditorProvider, useCurrentEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit';

import { mentionSuggestionOptions } from './MentionsPlugin/mentionSuggestionOptions'
import MenuBar from "./MenuBar"
import { useQuery } from '@apollo/client/react'
import { map as mapFp } from "lodash/fp"
import { map } from 'lodash'
import EnterToSend from './EnterToSend'
import { SuggestionPluginKey } from '@tiptap/suggestion'
import { GetAllEmployeesDocument, GetIssueCategoriesDocument, User } from '@/graphql/generated/graphql'
import { SpellCheckClickData, SpellCheck } from './SpellCheckPlugin'

const mapNode = mapFp('node')
const DEFAULT_EXTENSIONS = [
  StarterKit,
  TextStyle,
  Color,
]
interface PopoverState extends SpellCheckClickData {
  top: number;
  left: number;
}

const SpellCheckPopover: React.FC<{ popover: PopoverState | null, setPopover: (val: PopoverState | null) => void }> = ({ popover, setPopover }) => {
  const { editor } = useCurrentEditor();

  if (!popover || !editor) return null;

  const handleSelectSuggestion = (replacement: string) => {
    editor
      .chain()
      .focus()
      .insertContentAt(
        { from: popover.from, to: popover.to },
        replacement
      )
      .run();

    setPopover(null);
  };

  return (
    <Box
      className="spell-popover"
      sx={{
        position: 'fixed',
        zIndex: 50,
        top: `${popover.top}px`,
        left: `${popover.left}px`,
        minWidth: '160px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        padding: '4px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {popover.suggestions.length > 0 ? (
        popover.suggestions.slice(0, 5).map((suggestion: string) => (
          <Box
            key={suggestion}
            component="button"
            type="button"
            onClick={() => handleSelectSuggestion(suggestion)}
            sx={{
              display: 'block',
              width: '100%',
              padding: '6px 12px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              borderRadius: '4px',
              color: '#1f2937',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#f3f4f6',
              }
            }}
          >
            {suggestion}
          </Box>
        ))
      ) : (
        <Typography sx={{ padding: '6px 12px', color: '#9ca3af', fontStyle: 'italic', fontSize: '14px' }}>
          No suggestions found
        </Typography>
      )}
    </Box>
  );
};

interface TextEditorProps {
  text?: string,
  setText: (text: string) => void;
  options?: 'User' | 'IssueCategory'
  renderToolbar?: boolean;
  mentionChar?: string;
  saveOn?: 'blur' | 'update' | 'none'
  freeSolo?: boolean;
  enterToSave?: boolean
  helperText?: string
  minRows?: number;
};

const TextEditor: React.FC<TextEditorProps> = ({
  text = "",
  options,
  setText,
  renderToolbar = false,
  mentionChar = "@",
  saveOn = 'blur',
  freeSolo = false,
  enterToSave = false,
  helperText,
  minRows
}) => {
  const [popover, setPopover] = useState<PopoverState | null>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.spell-popover') && !target.closest('.spell-error')) {
        setPopover(null);
      }
    };
    window.addEventListener('mousedown', handleOutsideClick);
    return () => window.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const { data: employeesData, loading: loadingUser } = useQuery(GetAllEmployeesDocument, {
    skip: options !== 'User'
  });
  const { data, loading: loadingCategories } = useQuery(GetIssueCategoriesDocument, {
    skip: options !== 'IssueCategory',
    variables: { dynamic: false }
  })

  if (loadingUser || loadingCategories) return <Box sx={bundledStyling}> <LinearProgress /> </Box>

  const nodes: User[] = mapNode(employeesData?.getEmployees?.edges)
  const employeeOptions = map(nodes, ({ id, firstName, lastName }) => ({ __typename: "User" as const, id, firstName, lastName }))
  const categoryOptions = map(data?.getIssueCategories, ({ id, name }) => ({ __typename: "IssueCategory" as const, id, name }))

  const dynamicMinHeight = minRows ? `calc(${minRows} * 1.5em + 20px)` : '50px';
  console.log('minRows', minRows)

  const enterToSend = enterToSave ? [EnterToSend(setText)] : []

  const extensions = [
    ...DEFAULT_EXTENSIONS,
    ...enterToSend,
    Mention.configure({
      HTMLAttributes: {
        class: 'mention',
      },
      suggestion: {
        char: mentionChar,
        pluginKey: SuggestionPluginKey,
        ...mentionSuggestionOptions(options === 'User' ? employeeOptions : categoryOptions, freeSolo),
      }
    }),

    SpellCheck.configure({
      onSpellCheckClick: (data) => {
        setPopover({
          ...data,
          top: data.rect.bottom + 4,
          left: data.rect.left,
        });
      },
    }),
  ]

  return (
    <Box sx={[bundledStyling, {
      '& .tiptap': {
        minHeight: dynamicMinHeight,
      }
    }]}>
      <EditorProvider
        extensions={extensions}
        content={text}
        onBlur={({ editor }) => {
          if (saveOn === 'blur') {
            setText(editor.getHTML())
            if (enterToSave) editor.commands.clearContent();
          }
        }}
        onUpdate={({ editor }) => {
          if (saveOn === 'update' && !editor.isEmpty) {
            setText(editor.getHTML())
          }
        }}
        slotBefore={renderToolbar ? <MenuBar /> : null}
        slotAfter={<SpellCheckPopover popover={popover} setPopover={setPopover} />}
        editorProps={{
          attributes: {
            class: 'tiptap-editor',
            spellcheck: 'false',
          },
        }}
      />
      {helperText && <Typography variant='caption' position='absolute'> {helperText} </Typography>}
    </Box>
  )
}

/** @deprecated Use `objectLength` instead. */
export const TextEditorV2 = TextEditor

export default TextEditor

const bundledStyling = {
  width: '100%',
  border: "1px solid",
  borderColor: "grey.300",
  borderRadius: 2,
  background: 'white',
  '& .tiptap': {
    outline: 'none',
    minHeight: '50px',
    padding: '10px 15px',
    fontFamily: ["Roboto", "Helvetica", "Arial"],
    textAlign: 'left'
  },
  '& .tiptap p.is-editor-empty:first-child::before': {
    color: '#adb5bd',
    content: "attr(data-placeholder)",
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
  '& .mention': {
    backgroundColor: 'rgba(84, 160, 255, 0.2)',
    borderRadius: '0.4rem',
    boxDecorationBreak: 'clone',
    color: '#1976d2',
    padding: '0.1rem 0.3rem',
    fontWeight: 500,
  },
  // 7. Add Custom Styles for spelling errors directly into MUI's sx configuration
  '& .spell-error': {
    textDecoration: 'underline wavy #e02424',
    textUnderlineOffset: '4px',
    cursor: 'pointer',
  }
}
